import { c as _c } from "react/compiler-runtime";
import { getEntitiesByFpIdInvestigationsByInvestigationIdActiveSessionOptions } from '@onefootprint/fetch-dashboard';
import type { ActiveSessionResponseActive, ChatSessionResponse } from '@onefootprint/request-types/dashboard';
import { useQuery, useQueryClient } from '@tanstack/react-query';
export type AgentSessionInfo = {
  session?: ActiveSessionResponseActive | ChatSessionResponse;
  /**
   * Id of the underlying chat session, when one exists — e.g. a live autopilot
   * session's id. Lets the caller load that session's persisted messages.
   */
  chatSessionId?: string;
  /**
   * Set when bringing the session up failed (the active-session/liveness fetch — a
   * 403 here means a permission error — or minting/resuming the modal session).
   */
  error?: unknown;
  isLoading: boolean;
  handleConnectionLost?: () => void;
};

/**
 * Surfaces the investigation's existing autopilot session for the chat to display: a *live*
 * run (`kind: 'active'`) is a streaming modal session to connect to; a *finished* run
 * (`kind: 'no_active_session'`) exposes its chat session id so its transcript can be shown.
 * It does NOT start chat sessions — a new message creates a fresh default chat via
 * `useStartChat`, and the investigation rides along as resource context.
 */
export const useAgentSession = t0 => {
  const $ = _c(15);
  const {
    fpId,
    investigationId
  } = t0;
  const canLoad = !!fpId && !!investigationId;
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== fpId || $[1] !== investigationId) {
    t1 = getEntitiesByFpIdInvestigationsByInvestigationIdActiveSessionOptions({
      path: {
        fpId,
        investigationId
      }
    });
    $[0] = fpId;
    $[1] = investigationId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const options = t1;
  let t2;
  if ($[3] !== canLoad || $[4] !== options) {
    t2 = {
      ...options,
      enabled: canLoad,
      retry: false
    };
    $[3] = canLoad;
    $[4] = options;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const query = useQuery(t2);
  const data = query.data;
  const session = data?.kind === "active" ? data : undefined;
  const chatSessionId = session?.sessionId ?? (data?.kind === "no_active_session" ? data.chatSessionId : undefined);
  const t3 = canLoad && !query.isFetched;
  let t4;
  if ($[6] !== options || $[7] !== queryClient) {
    t4 = () => queryClient.invalidateQueries({
      queryKey: options.queryKey
    });
    $[6] = options;
    $[7] = queryClient;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== chatSessionId || $[10] !== query.error || $[11] !== session || $[12] !== t3 || $[13] !== t4) {
    t5 = {
      session,
      chatSessionId,
      isLoading: t3,
      error: query.error,
      handleConnectionLost: t4
    };
    $[9] = chatSessionId;
    $[10] = query.error;
    $[11] = session;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
};